(() => {
  if (window.ExcelQuoteSubmit) return;
  const endpoint = 'https://electrical-ai-0953ff-4ed961c1f4f7.herokuapp.com/api/website-quotes';
  const maxSize = 100 * 1024 * 1024;
  const allowed = /\.(pdf|dwg|xlsx?|docx?|jpe?g|png)$/i;
  const size = value => value < 1024 * 1024 ? `${Math.max(1, Math.round(value / 1024))} KB` : `${(value / (1024 * 1024)).toFixed(1)} MB`;

  async function request(path, options, fallback) {
    const response = await fetch(endpoint + path, {
      ...options,
      credentials: 'omit',
      signal: AbortSignal.timeout(options.body instanceof FormData ? 600000 : 60000)
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.error || fallback);
    return result;
  }

  function uploadFile(path, token, file, onProgress, fallback) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.upload.addEventListener('progress', event => {
        if (event.lengthComputable) onProgress(Math.min(1, event.loaded / event.total));
      });
      xhr.upload.addEventListener('load', () => onProgress(1));
      xhr.addEventListener('load', () => {
        const result = xhr.response || {};
        if (xhr.status >= 200 && xhr.status < 300) resolve(result);
        else reject(new Error(result.error || fallback));
      });
      ['error', 'timeout', 'abort'].forEach(event => xhr.addEventListener(event, () => reject(new Error(fallback))));
      xhr.open('POST', endpoint + path);
      xhr.responseType = 'json';
      xhr.timeout = 600000;
      xhr.withCredentials = false;
      xhr.setRequestHeader('X-Quote-Token', token);
      const body = new FormData();
      body.append('file', file, file.name);
      xhr.send(body);
    });
  }

  function progressScreen(root, form, status, messages, message) {
    const panel = root.querySelector('[data-quote-progress]');
    if (!panel) return { start() {}, phase() {}, transfer() {}, saving() {}, confirmed() {}, stop() {} };
    // Keep live progress outside the busy form, and use the native modal focus trap.
    form.after(panel);
    const title = panel.querySelector('[data-quote-progress-title]');
    const phase = panel.querySelector('[data-quote-progress-status]');
    const detail = panel.querySelector('[data-quote-progress-detail]');
    const help = panel.querySelector('[data-quote-progress-help]');
    const transfer = panel.querySelector('[data-quote-progress-transfer]');
    const bar = panel.querySelector('progress');
    const percent = panel.querySelector('[data-quote-progress-percent]');
    const meterLabel = panel.querySelector('[data-quote-progress-meter-label]');
    const bytes = panel.querySelector('[data-quote-progress-bytes]');
    const explanation = panel.querySelector('[data-quote-progress-explanation]');
    const receipt = panel.querySelector('[data-quote-progress-receipt]');
    const elapsed = panel.querySelector('[data-quote-progress-elapsed]');
    const steps = Array.from(panel.querySelectorAll('[data-quote-progress-step]'));
    const open = document.createElement('button');
    open.type = 'button';
    open.className = 'excel-quote-progress-toggle';
    open.dataset.quoteProgressOpen = '';
    open.textContent = messages.loading_view;
    open.hidden = true;
    status.after(open);
    let active = false;
    let timer;
    const unlockScroll = () => {
      if (!document.querySelector('.excel-quote-progress[open]')) document.documentElement.classList.remove('excel-quote-progress-open');
    };
    const show = () => {
      if (!active || panel.open) return;
      if (typeof panel.showModal === 'function') panel.showModal();
      else panel.setAttribute('open', '');
      document.documentElement.classList.add('excel-quote-progress-open');
      title.focus({ preventScroll: true });
    };
    const hide = () => {
      if (typeof panel.close === 'function') panel.close();
      else panel.removeAttribute('open');
      unlockScroll();
    };
    open.addEventListener('click', show);
    panel.querySelector('[data-quote-progress-hide]').addEventListener('click', hide);
    panel.addEventListener('close', () => {
      unlockScroll();
      if (active) open.focus({ preventScroll: true });
    });
    return {
      start() {
        active = true;
        open.hidden = false;
        const started = Date.now();
        const tick = () => {
          const seconds = Math.floor((Date.now() - started) / 1000);
          const time = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
          elapsed.textContent = message('loading_elapsed', { time });
          help.textContent = seconds >= 20 ? messages.loading_wait : messages.loading_help;
        };
        tick();
        timer = setInterval(tick, 1000);
        show();
      },
      phase(index, text, filename = '') {
        phase.textContent = text;
        detail.textContent = filename;
        detail.hidden = !filename;
        transfer.hidden = true;
        if (explanation) explanation.hidden = true;
        if (receipt) receipt.hidden = index === 0;
        steps.forEach((step, stepIndex) => {
          step.dataset.state = stepIndex < index ? 'complete' : stepIndex === index ? 'active' : 'waiting';
          if (stepIndex === index) step.setAttribute('aria-current', 'step');
          else step.removeAttribute('aria-current');
        });
      },
      transfer(loaded, total) {
        const value = Math.min(100, Math.floor(loaded / total * 100));
        transfer.hidden = false;
        transfer.dataset.mode = 'transfer';
        if (explanation) explanation.hidden = true;
        if (meterLabel) meterLabel.textContent = messages.loading_progress;
        bar.setAttribute('aria-label', messages.loading_progress);
        bar.value = value;
        percent.hidden = false;
        percent.textContent = `${value}%`;
        bytes.hidden = false;
        bytes.textContent = `${size(loaded)} / ${size(total)}`;
      },
      saving(text, filename) {
        this.phase(1, text, filename);
        transfer.hidden = false;
        transfer.dataset.mode = 'saving';
        if (meterLabel) meterLabel.textContent = messages.loading_confirming;
        // Browser transfer is finished. Only the server can confirm file receipt.
        bar.removeAttribute('value');
        bar.setAttribute('aria-label', messages.loading_confirming || text);
        percent.hidden = true;
        bytes.hidden = true;
        if (explanation) {
          explanation.textContent = messages.loading_save_help;
          explanation.hidden = false;
        }
      },
      confirmed(count, total) {
        if (receipt) receipt.textContent = message('loading_receipt', { count, total });
      },
      stop() {
        active = false;
        clearInterval(timer);
        open.hidden = true;
        hide();
      }
    };
  }

  function showConfirmation(root, values, selected, reference, message) {
    const panel = root.querySelector('[data-quote-success]');
    if (!panel) return false;
    const title = panel.querySelector('[data-quote-success-title]');
    panel.querySelector('[data-quote-success-body]').textContent = message('success_body', { company: values.company });
    panel.querySelector('[data-quote-success-company]').textContent = values.company;
    panel.querySelector('[data-quote-success-reference]').textContent = reference;
    panel.querySelector('[data-quote-success-count]').textContent = message(selected.length === 1 ? 'success_file_one' : 'success_file_many', { count: selected.length });
    const files = panel.querySelector('[data-quote-success-files]');
    files.replaceChildren();
    selected.forEach(file => {
      const item = document.createElement('li');
      const name = document.createElement('span');
      name.textContent = file.name;
      const bytes = document.createElement('span');
      bytes.className = 'excel-quote-success__file-size';
      bytes.textContent = size(file.size);
      item.append(name, bytes);
      files.append(item);
    });
    root.querySelectorAll('[data-quote-step], .ehq__header, [data-quote-upload-types]').forEach(element => { element.hidden = true; });
    const card = root.querySelector('[data-quote-card]');
    if (card) {
      card.style.height = '';
      card.classList.remove('is-resizing');
    }
    root.setAttribute('aria-labelledby', title.id);
    panel.hidden = false;
    return true;
  }

  function attach(root, getFiles) {
    const form = root.querySelector('[data-quote-form]');
    const status = root.querySelector('[data-quote-submit-status]');
    const submit = form.querySelector('[data-quote-submit]');
    const firstType = form.querySelector('[name="project_type"]');
    const messages = JSON.parse(form.querySelector('[data-quote-submit-messages]').textContent);
    const message = (key, values) => Object.entries(values).reduce((copy, [name, value]) => copy.replace(`{${name}}`, () => String(value)), messages[key]);
    if (form.dataset.quoteSubmitReady) return;
    form.dataset.quoteSubmitReady = 'true';
    submit.disabled = false;
    submit.removeAttribute('aria-disabled');
    let sending = false;
    let completed = false;
    let draft = null;
    const progress = progressScreen(root, form, status, messages, message);

    function announce(message, error = false) {
      status.textContent = message;
      status.hidden = !message;
      status.toggleAttribute('data-error', error);
      status.setAttribute('role', error ? 'alert' : 'status');
    }
    form.addEventListener('change', () => {
      firstType.setCustomValidity('');
      if (status.textContent === messages.project_type && form.querySelector('[name="project_type"]:checked')) announce('');
    });
    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (sending || completed) return;
      const types = Array.from(form.querySelectorAll('[name="project_type"]:checked')).map(input => input.value);
      firstType.setCustomValidity(types.length ? '' : messages.project_type);
      if (!types.length) {
        announce(messages.project_type, true);
        firstType.focus();
        return;
      }
      if (!form.reportValidity()) return;
      const selected = Array.from(getFiles());
      if (!selected.length || selected.length > 10 || selected.some(file => !allowed.test(file.name) || file.size <= 0 || file.size > maxSize) || selected.reduce((sum, file) => sum + file.size, 0) > 10 * maxSize) {
        announce(messages.files, true);
        status.focus();
        return;
      }
      const data = new FormData(form);
      const values = {
        name: String(data.get('name') || '').trim(),
        company: String(data.get('company') || '').trim(),
        phone: String(data.get('phone') || '').trim(),
        email: String(data.get('email') || '').trim(),
        description: String(data.get('description') || '').trim(),
        project_types: types,
        source_url: window.location.href,
        source_title: document.title,
        source_form: window.location.pathname === '/' ? 'Homepage quote form' : 'Quote request form',
        website: String(data.get('website') || ''),
        files: selected.map(file => ({ name: file.name, size: file.size }))
      };
      const fingerprint = JSON.stringify(values) + selected.map(file => file.lastModified).join(',');
      if (!draft || draft.fingerprint !== fingerprint) draft = { requestId: crypto.randomUUID(), fingerprint };
      sending = true;
      form.setAttribute('aria-busy', 'true');
      root.dataset.quoteSending = 'true';
      const controls = Array.from(root.querySelectorAll('input, textarea, button')).filter(control => !control.closest('[data-quote-progress]') && !control.hasAttribute('data-quote-progress-open'));
      const disabled = controls.map(control => control.disabled);
      controls.forEach(control => { control.disabled = true; });
      const buttonContents = Array.from(submit.childNodes).map(node => node.cloneNode(true));
      submit.textContent = messages.sending;
      announce(messages.preparing);
      let confirmed = false;
      try {
        progress.phase(0, messages.preparing);
        progress.start();
        const session = await request('/start', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...values, request_id: draft.requestId }) }, messages.fallback);
        if (session.complete !== true) {
          let received = session.uploaded.filter(uploaded => uploaded === true).length;
          progress.confirmed(received, selected.length);
          for (let index = 0; index < selected.length; index += 1) {
            if (session.uploaded[index] === true) continue;
            announce(message('uploading', { index: index + 1, count: selected.length, name: selected[index].name }));
            progress.phase(1, message('loading_upload', { index: index + 1, count: selected.length }), selected[index].name);
            progress.transfer(0, selected[index].size);
            const saving = () => {
              const text = message('loading_save', { index: index + 1, count: selected.length });
              announce(`${text}: ${selected[index].name}`);
              progress.saving(text, selected[index].name);
            };
            let uploaded = await uploadFile(`/${draft.requestId}/files/${index}?async=1`, session.token, selected[index], fraction => {
              if (fraction >= 1) saving();
              else progress.transfer(selected[index].size * fraction, selected[index].size);
            }, messages.fallback);
            if (uploaded.uploaded !== true) saving();
            const uploadDeadline = Date.now() + 330000;
            while (uploaded.uploaded !== true) {
              if (!uploaded.pending || Date.now() > uploadDeadline) throw new Error(messages.fallback);
              uploaded = await request(`/${draft.requestId}/files/${index}/status?wait=1`, { method: 'POST', headers: { 'X-Quote-Token': session.token } }, messages.fallback);
            }
            received += 1;
            progress.confirmed(received, selected.length);
          }
          announce(messages.submitting);
          progress.phase(2, messages.loading_final);
          let result = session.processing ? { pending: true } : await request(`/${draft.requestId}/submit?async=1`, { method: 'POST', headers: { 'X-Quote-Token': session.token } }, messages.fallback);
          const submitDeadline = Date.now() + 330000;
          while (result.pending) {
            if (Date.now() > submitDeadline) throw new Error(messages.fallback);
            result = await request(`/${draft.requestId}/status?wait=1`, { method: 'POST', headers: { 'X-Quote-Token': session.token } }, messages.fallback);
          }
          if (result.submitted !== true) throw new Error(messages.fallback);
        }
        completed = true;
        root.dataset.quoteSubmitted = 'true';
        confirmed = showConfirmation(root, values, selected, draft.requestId.slice(0, 8), message);
        if (!confirmed) {
          form.querySelectorAll('.ehq__fields, .eqc-fields, fieldset, .ehq__field--area, .ehq__field-hint, .eqc-description').forEach(element => { element.hidden = true; });
          const note = form.querySelector('[data-quote-privacy-note]');
          if (note) note.hidden = true;
          announce(message(selected.length === 1 ? 'success_one' : 'success_many', { reference: draft.requestId.slice(0, 8) }));
          submit.textContent = messages.sent;
        }
      } catch (error) {
        announce(error instanceof Error && !['TypeError', 'TimeoutError', 'AbortError'].includes(error.name) ? error.message : messages.fallback, true);
        controls.forEach((control, index) => { control.disabled = disabled[index]; });
        submit.replaceChildren(...buttonContents);
      } finally {
        sending = false;
        form.removeAttribute('aria-busy');
        delete root.dataset.quoteSending;
        progress.stop();
        if (confirmed) {
          const panel = root.querySelector('[data-quote-success]');
          panel.scrollIntoView({ block: 'start', behavior: 'auto' });
          panel.querySelector('[data-quote-success-title]').focus({ preventScroll: true });
        } else status.focus({ preventScroll: true });
      }
    });
  }
  window.ExcelQuoteSubmit = { attach };
  document.dispatchEvent(new Event('excel:quote-submit-ready'));
})();
